import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";

export default function NewsPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-20" data-testid="post-not-found">
      <p className="font-mono text-sm uppercase tracking-widest text-slate-500">Post not found</p>
      <Link to="/news" className="text-hivis underline underline-offset-4">Back to news</Link>
    </div>
  );
  if (!post) return <div className="flex min-h-screen items-center justify-center pt-20 font-mono text-sm uppercase tracking-widest text-slate-500">Loading...</div>;

  const postLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    image: post.image?.startsWith("http") ? post.image : `https://outdoorbannerframe.com${post.image}`,
    author: { "@type": "Organization", name: "The Banner Bar", url: "https://outdoorbannerframe.com" },
  };

  return (
    <div className="pt-20" data-testid={`news-post-${post.slug}`}>
      <Seo
        title={`${post.title} | The Banner Bar`}
        description={post.excerpt}
        path={`/news/${post.slug}`}
        image={post.image}
        jsonLd={postLd}
      />
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img src={post.image} alt={post.title} className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/30" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-4xl px-4 pb-10 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-hivis">{new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-5xl">{post.title}</h1>
          </Reveal>
        </div>
      </div>
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <Link to="/news" data-testid="back-to-news" className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-500 hover:text-hivis">
          <ArrowLeft className="h-3.5 w-3.5" /> All news
        </Link>
        <Reveal>
          <div className="space-y-4 text-sm leading-relaxed text-slate-300 md:text-base" data-testid="post-body">
            {post.body.split("\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
